import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";

import {
  Conversation,
  ConversationItem,
  DocumentConversation,
} from "common/types/conversation";
import {CurrentChat} from "./current_chat";
import {ChatHistory, ChatHistoryProps} from "./query_history";
import {hasPermission} from "utils/permissioner";
import {responseInProgress} from "./util/item_in_progress";
import {UploadTimeline} from "word/taskpane/routes/upload/components/upload_timeline";
import {DOCUMENT_FETCH_INTERVAL_MS} from "modules/documents/constants/document_fetch_interval";
import LoadingCircular from "common_components/loading_circular";
import renderProcessingUploadedDocumentStatus from "common_components/render_processing_document_status";

function getConversation(
  chats: DocumentConversation[],
  selectedConversation: DocumentConversation | undefined,
): DocumentConversation | undefined {
  const id = selectedConversation?.id;
  if (!id) {
    return undefined;
  }

  const conversation: DocumentConversation = chats[id];
  return conversation;
}

export default function render({
  document,
  fetchDocumentConversations,
  fetchDocumentConversationItems,
  generateLlmPrompt,
  scrollToClause,
  documentClauseparts,
  documentHeadings,
  updateDocumentConversationPrivacy,
  user,
  clearPanelData,
  updateConversationItemFeedback,
  issueId,
  issuePrompt,
  clearIssuePrompt,
  applyClauseChanges,
  appliedAmendments = {},
  clauseChangeErrors = [],
  setClauseChangeError,
  bulkApplyingIds,
  showLoadProgress,
  project,
  retryConversationItem,
  initialConversationId,
  applyDocumentAmendments,
  getIsTrackAllChangesOn,
  enableTrackAllChanges,
  onResponseComplete,
  highlightedConversationId,
  removeAppliedAmendments,
  onLogAmendmentErrors,
  compact = false,
}) {
  const {conversations} = document;
  const chatType = issueId ? "issue_chat" : "document_chat";

  const canMakeQueries = Boolean(
    hasPermission("can-perform-per-doc-llm-queries", user),
  );
  const canSendChatFeedback = Boolean(
    hasPermission("can-send-chat-feedback", user),
  );
  const chats = useMemo(
    () =>
      chatType === "document_chat"
        ? {
            ...(conversations?.["document_chat"] ?? {}),
            ...(conversations?.["issue_chat"] ?? {}),
          }
        : conversations?.[chatType] ?? {},
    [conversations?.["document_chat"], conversations?.["issue_chat"], chatType],
  );
  const [
    selectedConversation,
    setSelectedConversation,
  ] = useState<Conversation | null>(null);
  const [showCurrentChat, setShowCurrentChat] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAwaitingNewChat, setIsAwaitingNewChat] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const selectedConversationRef = useRef(null);
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const issuePromptProcessedRef = useRef(false);
  const refreshIntervalRef = useRef<number | null>(null);

  const selectNewEmptyConversation = () => {
    setSelectedConversation(null);
    setShowCurrentChat(true);
  };

  const selectConversation = conversation => {
    setSelectedConversation(conversation);
    setShowCurrentChat(true);
  };

  const clearSelectedConversation = () => {
    setSelectedConversation(null);
    setShowCurrentChat(false);
  };

  const getConversationChatType = conversationId => {
    return chats[conversationId]?.chat_type ?? chatType;
  };

  const getConversationItems = conversationId => {
    const type = getConversationChatType(conversationId);
    return conversations?.[type]?.[conversationId]?.items;
  };

  const previousConversationItem: ConversationItem | undefined =
    selectedConversation &&
    getConversationItems(selectedConversation.id)?.at(-1);

  const closePanel = async () => {
    await clearPanelData();
  };

  const onRetryConversationItem = async (item: ConversationItem) => {
    await retryConversationItem(item);
    setShouldRefresh(true);
  };

  useEffect(() => {
    fetchDocumentConversations(issueId);
    clearSelectedConversation();
  }, [issueId]);

  useEffect(() => {
    if (initialConversationId && chats[initialConversationId]) {
      selectConversation(chats[initialConversationId]);
    }
  }, [initialConversationId, chats[initialConversationId]]);

  useEffect(() => {
    if (selectedConversation?.id && !selectedConversation.isNew) {
      const type = getConversationChatType(selectedConversation.id);
      fetchDocumentConversationItems(
        selectedConversation.id,
        type,
        selectedConversation.first_item_id,
      );
    }
  }, [selectedConversation?.id, chatType, issueId]);

  useEffect(() => {
    const selectedType = getConversationChatType(selectedConversation?.id);
    const conversation =
      conversations?.[selectedType]?.[selectedConversation?.id];

    if (!shouldRefresh) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      setIsLoading(false);
    } else {
      setIsLoading(true);
      const id = setInterval(() => {
        fetchDocumentConversationItems(
          conversation.id,
          selectedType,
          conversation.first_item_id,
        )
          .then(result => {
            const items = result?.action?.payload?.conversation?.items;
            const lastItem = items?.at(-1);
            if (lastItem && !responseInProgress(lastItem)) {
              setShouldRefresh(false);
              if (onResponseComplete) {
                onResponseComplete();
              }
            }
          })
          .catch(() => {});
      }, 5000);

      refreshIntervalRef.current = (id as unknown) as number;

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    }
  }, [shouldRefresh]);

  useEffect(() => {
    const selectedType = getConversationChatType(selectedConversation?.id);
    const conversation =
      conversations?.[selectedType]?.[selectedConversation?.id];
    const recentMessage = conversation?.items?.at(-1);
    if (recentMessage && responseInProgress(recentMessage)) {
      setShouldRefresh(true);
    } else {
      if (shouldRefresh && onResponseComplete) {
        onResponseComplete();
      }
      setShouldRefresh(false);
      setTimeout(() => {
        textFieldRef?.current?.focus();
      }, 10);
    }
  }, [
    previousConversationItem?.id,
    previousConversationItem?.llm_meta?.is_complete,
    fetchDocumentConversationItems,
    chatType,
  ]);

  useEffect(() => {
    if (selectedConversationRef?.current) {
      const scrollHeight = selectedConversationRef.current.scrollHeight;
      const height = selectedConversationRef.current.clientHeight;
      const maxScrollTop = scrollHeight - height;
      selectedConversationRef.current.scrollTop =
        maxScrollTop > 0 ? maxScrollTop : 0;
    }
  }, [selectedConversation?.id, selectedConversation?.items?.length]);

  const generatePrompt = useCallback(
    async (textPrompt = prompt) => {
      setIsLoading(true);
      const selectedType = getConversationChatType(selectedConversation?.id);
      const selectedIssueId =
        chats[selectedConversation?.id]?.issue_id ?? issueId;
      const newConversation = await generateLlmPrompt(
        textPrompt,
        selectedType,
        selectedConversation?.id,
        getConversationItems(selectedConversation?.id)?.at(-1)?.id,
        selectedIssueId,
      );
      setShowCurrentChat(true);
      setPrompt("");
      setSelectedConversation({
        isNew: true,
        ...newConversation?.action?.payload?.conversation,
        chat_type: selectedType,
      });
    },
    [
      prompt,
      chatType,
      chats,
      selectedConversation,
      getConversationItems,
      generateLlmPrompt,
      issueId,
    ],
  );

  useEffect(() => {
    if (!issuePrompt) {
      issuePromptProcessedRef.current = false;
      return;
    }
    if (issuePromptProcessedRef.current) {
      return;
    }
    issuePromptProcessedRef.current = true;
    setSelectedConversation(null);
    setShowCurrentChat(true);
    setIsAwaitingNewChat(true);
    setTimeout(async () => {
      try {
        const newConversation = await generateLlmPrompt(
          issuePrompt,
          chatType,
          undefined,
          undefined,
          issueId,
        );
        setSelectedConversation({
          isNew: true,
          ...newConversation?.action?.payload?.conversation,
          chat_type: chatType,
        });
      } finally {
        setIsAwaitingNewChat(false);
        clearIssuePrompt();
      }
    }, 0); // prevent calling generatePrompt before fetch conversations call
  }, [issuePrompt, generateLlmPrompt, chatType, issueId]);

  const conversation = getConversation(chats, selectedConversation);
  return (
    <>
      {showLoadProgress ? (
        <div style={{margin: "0 0.5em 0.5em 0.5em"}}>
          {(project?.show_upload_timeline ?? false) === false ? (
            renderProcessingUploadedDocumentStatus(document)
          ) : (
            <UploadTimeline
              document={document}
              refreshTime={DOCUMENT_FETCH_INTERVAL_MS}
              isWebapp={true}
              collapse={true}
            />
          )}
        </div>
      ) : null}
      {showCurrentChat && conversation && !isAwaitingNewChat ? (
        <CurrentChat
          selectedConversationRef={selectedConversationRef}
          conversation={conversation}
          generateLlmPrompt={generatePrompt}
          prompt={prompt}
          setPrompt={setPrompt}
          isLoading={isLoading}
          textFieldRef={textFieldRef}
          onClose={clearSelectedConversation}
          onNewChat={selectNewEmptyConversation}
          documentClauseparts={documentClauseparts}
          documentHeadings={documentHeadings}
          scrollToClausepart={scrollToClause}
          updateDocumentConversationPrivacy={updateDocumentConversationPrivacy}
          user={user}
          canMakeQueries={canMakeQueries}
          canSendChatFeedback={canSendChatFeedback}
          sendFeedback={updateConversationItemFeedback}
          applyClauseChanges={applyClauseChanges}
          appliedAmendments={appliedAmendments}
          clauseChangeErrors={clauseChangeErrors}
          setClauseChangeError={setClauseChangeError}
          bulkApplyingIds={bulkApplyingIds}
          onRetryConversationItem={onRetryConversationItem}
          applyDocumentAmendments={applyDocumentAmendments}
          getIsTrackAllChangesOn={getIsTrackAllChangesOn}
          enableTrackAllChanges={enableTrackAllChanges}
          removeAppliedAmendments={removeAppliedAmendments}
          onLogAmendmentErrors={onLogAmendmentErrors}
          compact={compact}
        />
      ) : showCurrentChat && (isLoading || isAwaitingNewChat) ? (
        <LoadingCircular />
      ) : (
        <ChatHistory
          {...(({
            chats,
            selectedConversation,
            setSelectedConversation: selectConversation,
            onNewChat: selectNewEmptyConversation,
            generatePrompt,
            prompt,
            setPrompt,
            isLoading,
            textFieldRef,
            currentUser: user,
            onClose: closePanel,
            canMakeQueries,
            chatType,
            allowBackwardsNavigation: !showLoadProgress,
          } as unknown) as ChatHistoryProps)}
          highlightedConversationId={highlightedConversationId}
          compact={compact}
        />
      )}
    </>
  );
}
